import { useEffect, useRef, useState } from 'react';

/**
 * Renders sanitized HTML inline in chat with form submit interception.
 * When a form is submitted, field values are serialized and sent as a chat message.
 */
export function HtmlBlock({ html, id, onFormSubmit }) {
  const containerRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    // Find all forms and attach submit handlers
    const forms = containerRef.current.querySelectorAll('form');
    const handlers = [];

    forms.forEach(form => {
      const handler = (e) => {
        e.preventDefault();
        if (submitted) return;

        // Serialize form data
        const formData = new FormData(form);
        const parts = [];

        for (const [name, value] of formData.entries()) {
          if (!value && value !== 0) continue;
          // For checkboxes, the value is "on" by default — use the name as the value
          const input = form.querySelector(`[name="${name}"]`);
          if (input?.type === 'checkbox') {
            parts.push(name);
          } else {
            parts.push(`${name}=${value}`);
          }
        }

        // Also collect unchecked checkboxes that we should note
        const checkboxes = form.querySelectorAll('input[type="checkbox"]');
        // Only include checked ones (already in formData)

        if (parts.length === 0) {
          parts.push('(no changes)');
        }

        const message = `Run with: ${parts.join(', ')}`;
        setSubmitted(true);

        // Disable all inputs after submission
        form.querySelectorAll('input, select, textarea, button').forEach(el => {
          el.disabled = true;
        });

        if (onFormSubmit) {
          onFormSubmit(message);
        }
      };

      form.addEventListener('submit', handler);
      handlers.push({ form, handler });
    });

    return () => {
      handlers.forEach(({ form, handler }) => {
        form.removeEventListener('submit', handler);
      });
    };
  }, [html, onFormSubmit, submitted]);

  return (
    <div
      ref={containerRef}
      className="my-3 rounded-lg border border-gray-700 overflow-hidden"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
